import {
    createContext,
    useContext,
    createSignal,
} from 'solid-js'

const ContactSupportModalContext = createContext()

/**
 * Provides app-wide access to the Contact Support modal so any surface
 * (e.g. a list item in booking/stay details) can open it without managing
 * its own visibility state. Mirrors the AuthModalProvider pattern.
 */
export function ContactSupportModalProvider (props) {
    const [
        showContactSupportModal,
        setShowContactSupportModal,
    ] = createSignal(false)
    const [
        bookingId,
        setBookingId,
    ] = createSignal()
    const [
        source,
        setSource,
    ] = createSignal()
    // TENANT-5570 — a tax dispute is about a property, not a booking: the Tax
    // rates modal opens support with { property_id, subject: 'tax_dispute' }.
    const [
        propertyId,
        setPropertyId,
    ] = createSignal()
    const [
        subject,
        setSubject,
    ] = createSignal()
    // Set only by callers that opened this modal from somewhere the host can
    // sensibly go back to (the wizard's help modal). Entry points with nowhere to
    // return to — booking details, stay details — leave it unset and get no back
    // button, exactly as before.
    const [
        onBack,
        setOnBack,
    ] = createSignal()

    // Owned here rather than inside the modal: the confirmation has to outlive
    // the modal, which unmounts the moment the request succeeds.
    const [
        messageSent,
        setMessageSent,
    ] = createSignal(false)

    const api = {
        bookingId,
        closeContactSupportModal: () => {
            setShowContactSupportModal(false)
            setBookingId()
            setPropertyId()
            setSubject()
            setSource()
            setOnBack()
        },
        dismissMessageSent: () => setMessageSent(false),
        messageSent,
        notifyMessageSent: () => setMessageSent(true),
        onBack,
        /**
         * @param {{ booking_id?: string, property_id?: string, subject?: string, source?: string, onBack?: () => void }} [context]
         */
        openContactSupportModal: (context = {}) => {
            setBookingId(context.booking_id ?? undefined)
            setPropertyId(context.property_id ?? undefined)
            setSubject(context.subject ?? undefined)
            setSource(context.source ?? undefined)
            // Stored via an updater so Solid keeps the function as state rather
            // than invoking it as a lazy initialiser.
            setOnBack(() => context.onBack)
            setShowContactSupportModal(true)
        },
        propertyId,
        showContactSupportModal,
        source,
        subject,
    }

    return (
        <ContactSupportModalContext.Provider value={api}>
            {props.children}
        </ContactSupportModalContext.Provider>
    )
}

export function useContactSupportModal () {
    const context = useContext(ContactSupportModalContext)
    if (!context) {
        throw new Error('useContactSupportModal must be used within a ContactSupportModalProvider')
    }
    return context
}
